import { useCallback, useEffect, useMemo, useState } from 'react'

type Row = Record<string, unknown> & { DT_RowId?: unknown }

/**
 * Randul desfasurat tinut de pagina (`DataTable` `openRow` / `onOpenRow`), ca
 * in lista de tichete. Lasat tabelului, randul se inchide la orice raspuns nou,
 * iar o lista recitita singura (coada Incidents, la minut) il scotea pe om din
 * ce citea.
 *
 * Aici o recitire nu-l inchide. Daca randul a iesit din raspuns (incidentul
 * inchis de altcineva), ramane pe loc, cu ultimele valori citite, pana il
 * inchide omul. O vedere noua (`view`: pagina, sortare, cautare, filtre) il
 * inchide, ca la DataTables: acolo hotaraste omul ce vrea sa vada.
 */
export function useHeldOpenRow<R extends Row>(fetched: R[], view: string) {
  const [held, setHeld] = useState<{ rid: string; row: R; at: number } | null>(null)

  const rows = useMemo(() => {
    if (held === null || fetched.some((r) => String(r.DT_RowId) === held.rid)) return fetched
    const kept = [...fetched]
    kept.splice(Math.min(held.at, kept.length), 0, held.row)
    return kept
  }, [fetched, held])

  // cat timp randul e in raspuns, cel tinut minte se tine la zi
  useEffect(() => {
    setHeld((was) => {
      if (was === null) return was
      const at = fetched.findIndex((r) => String(r.DT_RowId) === was.rid)
      return at < 0 || fetched[at] === was.row ? was : { rid: was.rid, row: fetched[at], at }
    })
  }, [fetched])

  useEffect(() => setHeld(null), [view])

  const onOpenRow = useCallback(
    (rid: string | null) => {
      if (rid === null) {
        setHeld(null)
        return
      }
      const at = rows.findIndex((r) => String(r.DT_RowId) === rid)
      if (at >= 0) setHeld({ rid, row: rows[at], at })
    },
    [rows],
  )

  return { rows, openRow: held?.rid ?? null, onOpenRow }
}
